import React, { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { cmsService, Route as RouteType } from '@services/api'
import {
  Route,
  Plus,
  Search,
  MapPin,
  DollarSign,
  Download,
  Eye,
  Edit,
  Trash2,
  MoreVertical,
  Navigation,
  Calendar,
  Users,
  TrendingUp,
  Filter,
  X,
  ArrowUp,
  ArrowDown,
  GripVertical
} from 'lucide-react'
import {
  VStack,
  HStack,
  Text,
  Heading,
  Button,
  Input,
  InputGroup,
  InputLeftElement,
  Card,
  CardBody,
  CardHeader,
  Center,
  Flex,
  useColorModeValue,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  SimpleGrid,
  Box,
  Avatar,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Select,
  Textarea,
  NumberInput,
  NumberInputField,
  useToast,
  Divider,
  Tag,
  TagLabel,
  TagCloseButton,
  Tooltip,
  Spinner,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay
} from '@chakra-ui/react'

// Types for route data
interface RouteData {
  id: string;
  name: string;
  routeCode: string;
  startPoint: string;
  endPoint: string;
  roadNames?: string[];
  distanceKm?: number;
  estimatedDurationMinutes?: number;
  baseFare?: number;
  active: boolean;
  createdAt: string;
  updatedAt: string;
  stops?: RouteStopDisplay[];
  statistics?: {
    totalTrips: number;
    totalStops: number;
  };
}

interface RouteStopDisplay {
  id: string;
  name: string;
  latitude: number;
  longitude: number;
  address?: string;
  stopOrder: number;
  distanceFromStart?: number;
  fareFromStart?: number;
}

interface StopData {
  id?: string
  name: string
  latitude: number
  longitude: number
  address?: string
  distanceFromStart?: number
  fareFromStart?: number
  order: number // Stop order in the route sequence
}

interface RouteFormData {
  name: string
  routeCode: string
  startPoint: string
  endPoint: string
  roadNames: string
  distanceKm: number
  estimatedDurationMinutes: number
  baseFare: number
  stops: StopData[]
}

/** Aligns with routes-service `RouteService.calculateFareFromDistance` (tiered + round to 500). */
const TIER_THRESHOLD_KM = 10
const PRICE_PER_KM_AFTER_THRESHOLD = 200
const FALLBACK_BASE_FARE = 2000

function roundFareTo500(fare: number): number {
  return Math.round(fare / 500) * 500
}

/**
 * Cumulative "fare from start" for a stop at distanceKm along the route.
 * First stop (0 km) = 0 so segment fares are (fareAtB - fareAtA).
 */
function fareFromStartForStop(distanceKm: number, routeBaseFare: number): number {
  const d = Math.max(0, Number(distanceKm) || 0)
  if (d <= 0) return 0
  const base = routeBaseFare > 0 ? routeBaseFare : FALLBACK_BASE_FARE
  let fare: number
  if (d <= TIER_THRESHOLD_KM) {
    fare = base
  } else {
    fare = base + (d - TIER_THRESHOLD_KM) * PRICE_PER_KM_AFTER_THRESHOLD
  }
  return roundFareTo500(fare)
}

// View Route Modal
// Route Delete Confirmation Dialog
function RouteDeleteDialog({ isOpen, onClose, onConfirm, route }: { 
  isOpen: boolean; 
  onClose: () => void; 
  onConfirm: () => void;
  route: any;
}) {
  const cancelRef = React.useRef<HTMLButtonElement>(null)
  const [isDeleting, setIsDeleting] = useState(false)

  const handleDelete = async () => {
    setIsDeleting(true)
    await onConfirm()
    setIsDeleting(false)
  }

  if (!route) return null

  return (
    <AlertDialog
      isOpen={isOpen}
      leastDestructiveRef={cancelRef}
      onClose={onClose}
    >
      <AlertDialogOverlay>
        <AlertDialogContent borderRadius="xl">
          <AlertDialogHeader fontSize="lg" fontWeight="bold">
            Delete Route
          </AlertDialogHeader>

          <AlertDialogBody>
            Are you sure you want to delete route <strong>{route.routeName || route.name}</strong>? 
            This action cannot be undone and will remove all associated stops and fares.
          </AlertDialogBody>

          <AlertDialogFooter>
            <Button ref={cancelRef} onClick={onClose} borderRadius="xl">
              Cancel
            </Button>
            <Button 
              colorScheme="red" 
              onClick={handleDelete} 
              ml={3} 
              isLoading={isDeleting} 
              loadingText="Deleting..."
              borderRadius="xl"
            >
              Delete
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialogOverlay>
    </AlertDialog>
  )
}

function RouteViewModal({ isOpen, onClose, route }: { isOpen: boolean; onClose: () => void; route: any }) {
  if (!route) return null

  return (
    <Modal isOpen={isOpen} onClose={onClose} size={{ base: 'full', md: '2xl' }}>
      <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(10px)" />
      <ModalContent borderRadius={{ base: 'none', md: '2xl' }} m={{ base: 0, md: 4 }}>
        <ModalHeader>
          <HStack spacing={3}>
            <Box p={2} bg="brand.100" borderRadius="lg">
              <Route size={20} color="var(--chakra-colors-brand-600)" />
            </Box>
            <VStack align="start" spacing={0}>
              <Text fontSize="xl" fontWeight="bold">{route.name}</Text>
              <Text fontSize="sm" color="gray.600">Route Details</Text>
            </VStack>
          </HStack>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          <VStack spacing={6} align="stretch">
            <Card bg="gray.50" border="1px solid" borderColor="gray.200">
              <CardBody>
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                  <VStack align="start" spacing={2}>
                    <Text fontSize="sm" fontWeight="medium" color="gray.600">Route Code</Text>
                    <Text fontSize="lg" fontWeight="semibold" color="gray.900">{route.routeCode}</Text>
                  </VStack>
                  <VStack align="start" spacing={2}>
                    <Text fontSize="sm" fontWeight="medium" color="gray.600">Status</Text>
                    <Badge
                      colorScheme={route.active ? 'green' : 'gray'}
                      variant="subtle"
                      borderRadius="full"
                      px={3}
                      py={1}
                    >
                      {route.active ? 'Active' : 'Inactive'}
                    </Badge>
                  </VStack>
                </SimpleGrid>
              </CardBody>
            </Card>

            <Card bg="gray.50" border="1px solid" borderColor="gray.200">
              <CardBody>
                <VStack spacing={4} align="stretch">
                  <HStack spacing={1} fontSize="md" color="gray.600">
                    <MapPin size={16} />
                    <Text fontWeight="medium">Route Path</Text>
                  </HStack>
                  <HStack spacing={4} align="center">
                    <VStack align="start" spacing={1} flex={1}>
                      <Text fontSize="sm" color="gray.600">Start Point</Text>
                      <Text fontSize="lg" fontWeight="semibold" color="gray.900">{route.startPoint}</Text>
                    </VStack>
                    <Box color="gray.400">
                      <Navigation size={20} />
                    </Box>
                    <VStack align="start" spacing={1} flex={1}>
                      <Text fontSize="sm" color="gray.600">End Point</Text>
                      <Text fontSize="lg" fontWeight="semibold" color="gray.900">{route.endPoint}</Text>
                    </VStack>
                  </HStack>
                </VStack>
              </CardBody>
            </Card>

            {route.roadNames && route.roadNames.length > 0 && (
              <Card bg="gray.50" border="1px solid" borderColor="gray.200">
                <CardBody>
                  <VStack align="start" spacing={2}>
                    <Text fontSize="sm" fontWeight="medium" color="gray.600">Roads Covered</Text>
                    <HStack flexWrap="wrap" gap={2}>
                      {route.roadNames.map((road: string, idx: number) => (
                        <Tag key={idx} size="md" colorScheme="blue" borderRadius="full">
                          <TagLabel>{road}</TagLabel>
                        </Tag>
                      ))}
                    </HStack>
                  </VStack>
                </CardBody>
              </Card>
            )}

            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
              <Card bg="gray.50" border="1px solid" borderColor="gray.200">
                <CardBody>
                  <VStack align="start" spacing={2}>
                    <Text fontSize="sm" fontWeight="medium" color="gray.600">Distance</Text>
                    <Text fontSize="lg" fontWeight="semibold" color="gray.900">
                      {route.distanceKm ? `${route.distanceKm} km` : 'N/A'}
                    </Text>
                  </VStack>
                </CardBody>
              </Card>
              <Card bg="gray.50" border="1px solid" borderColor="gray.200">
                <CardBody>
                  <VStack align="start" spacing={2}>
                    <Text fontSize="sm" fontWeight="medium" color="gray.600">Duration</Text>
                    <Text fontSize="lg" fontWeight="semibold" color="gray.900">
                      {route.estimatedDurationMinutes ? `${route.estimatedDurationMinutes} min` : 'N/A'}
                    </Text>
                  </VStack>
                </CardBody>
              </Card>
            </SimpleGrid>

            <Card bg="gray.50" border="1px solid" borderColor="gray.200">
              <CardBody>
                <VStack align="start" spacing={2}>
                  <Text fontSize="sm" fontWeight="medium" color="gray.600">Base Fare</Text>
                  <Text fontSize="lg" fontWeight="semibold" color="gray.900">
                    {route.baseFare ? `TZS ${route.baseFare.toLocaleString()}` : 'N/A'}
                  </Text>
                </VStack>
              </CardBody>
            </Card>

            {route.stops && route.stops.length > 0 && (
              <Card bg="gray.50" border="1px solid" borderColor="gray.200">
                <CardBody>
                  <VStack align="start" spacing={3}>
                    <Text fontSize="sm" fontWeight="medium" color="gray.600">Stops ({route.stops.length})</Text>
                    <VStack spacing={2} align="stretch" w="full">
                      {route.stops.map((stop: any, index: number) => (
                        <HStack key={index} p={3} bg="white" borderRadius="lg" spacing={3}>
                          <Box
                            minW="32px"
                            h="32px"
                            bg="brand.100"
                            color="brand.600"
                            borderRadius="full"
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            fontWeight="bold"
                            fontSize="sm"
                          >
                            {stop.stopOrder}
                          </Box>
                          <VStack align="start" spacing={0} flex={1}>
                            <Text fontWeight="medium" color="gray.900">{stop.name}</Text>
                            {stop.address && (
                              <Text fontSize="xs" color="gray.600">{stop.address}</Text>
                            )}
                          </VStack>
                        </HStack>
                      ))}
                    </VStack>
                  </VStack>
                </CardBody>
              </Card>
            )}

            <HStack spacing={3} w="full" pt={4}>
              <Button variant="outline" onClick={onClose} flex={1} borderRadius="xl">
                Close
              </Button>
            </HStack>
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  )
}

// Edit Route Modal
function RouteEditModal({ isOpen, onClose, route }: { isOpen: boolean; onClose: () => void; route: any }) {
  const [formData, setFormData] = useState<RouteFormData>({
    name: '',
    routeCode: '',
    startPoint: '',
    endPoint: '',
    roadNames: '',
    distanceKm: 0,
    estimatedDurationMinutes: 0,
    baseFare: FALLBACK_BASE_FARE,
    stops: []
  })
  const [isLoading, setIsLoading] = useState(false)
  const toast = useToast()
  const queryClient = useQueryClient()

  // Update form data when route changes
  React.useEffect(() => {
    if (route) {
      const bf = Number(route.baseFare) || FALLBACK_BASE_FARE
      setFormData({
        name: route.name || '',
        routeCode: route.routeCode || '',
        startPoint: route.startPoint || '',
        endPoint: route.endPoint || '',
        roadNames: route.roadNames ? route.roadNames.join(', ') : '',
        distanceKm: route.distanceKm || 0,
        estimatedDurationMinutes: route.estimatedDurationMinutes || 0,
        baseFare: bf,
        stops: route.stops ? route.stops.map((s: any) => {
          const dist = Number(s.distanceFromStart) || 0
          return {
            name: s.name,
            latitude: s.latitude,
            longitude: s.longitude,
            address: s.address,
            distanceFromStart: dist,
            fareFromStart: fareFromStartForStop(dist, bf),
            order: s.stopOrder
          }
        }) : []
      })
    }
  }, [route])

  // Stops management functions
  const addStop = () => {
    const newStop: StopData = {
      name: '',
      latitude: 0,
      longitude: 0,
      address: '',
      distanceFromStart: 0,
      fareFromStart: 0,
      order: formData.stops.length + 1
    }
    setFormData({
      ...formData,
      stops: [...formData.stops, newStop]
    })
  }

  const removeStop = (index: number) => {
    const updatedStops = formData.stops.filter((_, i) => i !== index)
    // Reorder remaining stops
    const reorderedStops = updatedStops.map((stop, i) => ({
      ...stop,
      order: i + 1
    }))
    setFormData({
      ...formData,
      stops: reorderedStops
    })
  }

  const updateStop = (index: number, field: keyof StopData, value: any) => {
    setFormData((prev) => {
      const updatedStops = [...prev.stops]
      const next = { ...updatedStops[index], [field]: value } as StopData
      if (field === 'distanceFromStart') {
        next.fareFromStart = fareFromStartForStop(Number(value) || 0, prev.baseFare || FALLBACK_BASE_FARE)
      }
      updatedStops[index] = next
      return { ...prev, stops: updatedStops }
    })
  }

  const moveStopUp = (index: number) => {
    if (index === 0) return
    setFormData((prev) => {
      const updatedStops = [...prev.stops]
      const temp = updatedStops[index]
      updatedStops[index] = updatedStops[index - 1]
      updatedStops[index - 1] = temp
      const reorderedStops = updatedStops.map((stop, i) => ({
        ...stop,
        order: i + 1
      }))
      return { ...prev, stops: reorderedStops }
    })
  }

  const moveStopDown = (index: number) => {
    setFormData((prev) => {
      if (index === prev.stops.length - 1) return prev
      const updatedStops = [...prev.stops]
      const temp = updatedStops[index]
      updatedStops[index] = updatedStops[index + 1]
      updatedStops[index + 1] = temp
      const reorderedStops = updatedStops.map((stop, i) => ({
        ...stop,
        order: i + 1
      }))
      return { ...prev, stops: reorderedStops }
    })
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)

    try {
      // Validate stops data
      const hasValidStops = formData.stops.every(stop =>
        stop.name.trim() !== '' &&
        stop.latitude !== 0 &&
        stop.longitude !== 0
      )

      if (formData.stops.length > 0 && !hasValidStops) {
        toast({
          title: 'Validation Error',
          description: 'Please ensure all stops have valid names and coordinates.',
          status: 'error',
          duration: 3000,
          isClosable: true,
        })
        setIsLoading(false)
        return
      }

      const bf = formData.baseFare || FALLBACK_BASE_FARE
      const stopsWithFares = formData.stops.map((s) => ({
        ...s,
        fareFromStart: fareFromStartForStop(s.distanceFromStart || 0, bf)
      }))

      await cmsService.updateRoute(route.id, {
        name: formData.name,
        routeCode: formData.routeCode,
        startPoint: formData.startPoint,
        endPoint: formData.endPoint,
        roadNames: formData.roadNames.split(',').map(r => r.trim()).filter(Boolean),
        distanceKm: formData.distanceKm,
        estimatedDurationMinutes: formData.estimatedDurationMinutes,
        baseFare: formData.baseFare,
        stops: stopsWithFares
      })

      queryClient.invalidateQueries('routes')

      toast({
        title: 'Route Updated',
        description: `${formData.name} has been successfully updated.`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      })

      onClose()
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error?.response?.data?.message || 'Failed to update route. Please try again.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      })
    } finally {
      setIsLoading(false)
    }
  }

  if (!route) return null

  return (
    <Modal isOpen={isOpen} onClose={onClose} size={{ base: 'full', md: 'xl' }}>
      <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(10px)" />
      <ModalContent borderRadius={{ base: 'none', md: '2xl' }} m={{ base: 0, md: 4 }} maxH={{ base: '100vh', md: '90vh' }}>
        <ModalHeader>
          <HStack spacing={3}>
            <Box p={2} bg="brand.100" borderRadius="lg">
              <Edit size={20} color="var(--chakra-colors-brand-600)" />
            </Box>
            <Text fontSize="xl" fontWeight="bold">Edit Route</Text>
          </HStack>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6} overflowY="auto">
          <form onSubmit={handleSubmit}>
            <VStack spacing={4}>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} w="full">
                <FormControl isRequired>
                  <FormLabel fontSize="sm" fontWeight="medium">Route Name</FormLabel>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g., Mwenge - Ubungo"
                    borderRadius="xl"
                  />
                </FormControl>

                <FormControl>
                  <FormLabel fontSize="sm" fontWeight="medium">Route Code</FormLabel>
                  <Input
                    value={formData.routeCode}
                    onChange={(e) => setFormData({ ...formData, routeCode: e.target.value })}
                    placeholder="e.g., R001"
                    borderRadius="xl"
                  />
                </FormControl>
              </SimpleGrid>

              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} w="full">
                <FormControl isRequired>
                  <FormLabel fontSize="sm" fontWeight="medium">Start Point</FormLabel>
                  <Input
                    value={formData.startPoint}
                    onChange={(e) => setFormData({ ...formData, startPoint: e.target.value })}
                    placeholder="e.g., Mwenge"
                    borderRadius="xl"
                  />
                </FormControl>

                <FormControl isRequired>
                  <FormLabel fontSize="sm" fontWeight="medium">End Point</FormLabel>
                  <Input
                    value={formData.endPoint}
                    onChange={(e) => setFormData({ ...formData, endPoint: e.target.value })}
                    placeholder="e.g., Ubungo"
                    borderRadius="xl"
                  />
                </FormControl>
              </SimpleGrid>

              <FormControl>
                <FormLabel fontSize="sm" fontWeight="medium">Road Names (comma separated)</FormLabel>
                <Input
                  value={formData.roadNames}
                  onChange={(e) => setFormData({ ...formData, roadNames: e.target.value })}
                  placeholder="e.g., Ali Hassan Mwinyi Road, New Bagamoyo Road"
                  borderRadius="xl"
                />
              </FormControl>

              <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4} w="full">
                <FormControl>
                  <FormLabel fontSize="sm" fontWeight="medium">Distance (km)</FormLabel>
                  <Input
                    type="number"
                    value={formData.distanceKm}
                    onChange={(e) => setFormData({ ...formData, distanceKm: Number(e.target.value) })}
                    placeholder="0"
                    borderRadius="xl"
                  />
                </FormControl>

                <FormControl>
                  <FormLabel fontSize="sm" fontWeight="medium">Duration (min)</FormLabel>
                  <Input
                    type="number"
                    value={formData.estimatedDurationMinutes}
                    onChange={(e) => setFormData({ ...formData, estimatedDurationMinutes: Number(e.target.value) })}
                    placeholder="0"
                    borderRadius="xl"
                  />
                </FormControl>

                <FormControl>
                  <FormLabel fontSize="sm" fontWeight="medium">Base Fare / Minimum Fare (TZS)</FormLabel>
                  <Input
                    type="number"
                    value={formData.baseFare}
                    onChange={(e) => {
                      const v = Number(e.target.value) || FALLBACK_BASE_FARE
                      setFormData((prev) => ({
                        ...prev,
                        baseFare: v,
                        stops: prev.stops.map((s) => ({
                          ...s,
                          fareFromStart: fareFromStartForStop(s.distanceFromStart || 0, v),
                        })),
                      }))
                    }}
                    placeholder="2000"
                    borderRadius="xl"
                  />
                  <Text fontSize="xs" color="gray.500" mt={1}>
                    Tiered model: this amount covers up to {TIER_THRESHOLD_KM} km from the start; beyond that add TZS {PRICE_PER_KM_AFTER_THRESHOLD}/km (rounded to nearest 500). First stop stays 0 TZS.
                  </Text>
                </FormControl>
              </SimpleGrid>

              {/* Stops Management Section */}
              <Box w="full" pt={4}>
                <HStack justify="space-between" mb={4}>
                  <Heading size="md" color="gray.900">
                    Route Stops
                  </Heading>
                  <Button
                    leftIcon={<Plus size={16} />}
                    size="sm"
                    colorScheme="brand"
                    variant="outline"
                    onClick={addStop}
                  >
                    Add Stop
                  </Button>
                </HStack>

                {formData.stops.length === 0 ? (
                  <Card bg="gray.50" border="2px dashed" borderColor="gray.200">
                    <CardBody>
                      <Center py={8}>
                        <VStack spacing={4}>
                          <Box p={3} bg="gray.100" borderRadius="full">
                            <MapPin size={24} color="var(--chakra-colors-gray-500)" />
                          </Box>
                          <VStack spacing={2}>
                            <Text fontWeight="medium" color="gray.700">
                              No stops added yet
                            </Text>
                            <Text fontSize="sm" color="gray.500" textAlign="center">
                              Add stops along the route to define the complete journey
                            </Text>
                          </VStack>
                          <Button
                            leftIcon={<Plus size={16} />}
                            colorScheme="brand"
                            variant="outline"
                            onClick={addStop}
                            size="sm"
                          >
                            Add First Stop
                          </Button>
                        </VStack>
                      </Center>
                    </CardBody>
                  </Card>
                ) : (
                  <VStack spacing={3} align="stretch">
                    {formData.stops.map((stop, index) => (
                      <Card key={index} bg="white" border="1px solid" borderColor="gray.200">
                        <CardBody>
                          <HStack spacing={4} align="start">
                            {/* Order indicator and drag handle */}
                            <VStack spacing={1}>
                              <Box
                                p={2}
                                bg="brand.100"
                                color="brand.600"
                                borderRadius="md"
                                fontSize="sm"
                                fontWeight="bold"
                                minW="32px"
                                textAlign="center"
                              >
                                {stop.order}
                              </Box>
                              <HStack spacing={1}>
                                <IconButton
                                  aria-label="Move up"
                                  icon={<ArrowUp size={12} />}
                                  size="xs"
                                  variant="ghost"
                                  onClick={() => moveStopUp(index)}
                                  isDisabled={index === 0}
                                />
                                <IconButton
                                  aria-label="Move down"
                                  icon={<ArrowDown size={12} />}
                                  size="xs"
                                  variant="ghost"
                                  onClick={() => moveStopDown(index)}
                                  isDisabled={index === formData.stops.length - 1}
                                />
                              </HStack>
                            </VStack>

                            {/* Stop details */}
                            <VStack spacing={3} flex={1} align="stretch">
                              <HStack spacing={3}>
                                <FormControl flex={1}>
                                  <FormLabel fontSize="sm">Stop Name</FormLabel>
                                  <Input
                                    value={stop.name}
                                    onChange={(e) => updateStop(index, 'name', e.target.value)}
                                    placeholder="e.g., Mwenge"
                                    size="sm"
                                  />
                                </FormControl>
                                <FormControl flex={1}>
                                  <FormLabel fontSize="sm">Address/Landmark</FormLabel>
                                  <Input
                                    value={stop.address || ''}
                                    onChange={(e) => updateStop(index, 'address', e.target.value)}
                                    placeholder="e.g., Near Mwenge Market"
                                    size="sm"
                                  />
                                </FormControl>
                              </HStack>

                              <HStack spacing={3}>
                                <FormControl flex={1}>
                                  <FormLabel fontSize="sm">Latitude</FormLabel>
                                  <Input
                                    type="number"
                                    value={stop.latitude}
                                    onChange={(e) => updateStop(index, 'latitude', Number(e.target.value) || 0)}
                                    placeholder="0.00000000"
                                    size="sm"
                                    step="0.0000001"
                                  />
                                </FormControl>
                                <FormControl flex={1}>
                                  <FormLabel fontSize="sm">Longitude</FormLabel>
                                  <Input
                                    type="number"
                                    value={stop.longitude}
                                    onChange={(e) => updateStop(index, 'longitude', Number(e.target.value) || 0)}
                                    placeholder="0.00000000"
                                    size="sm"
                                    step="0.0000001"
                                  />
                                </FormControl>
                              </HStack>

                              <HStack spacing={3}>
                                <FormControl flex={1}>
                                  <FormLabel fontSize="sm">Distance from Start (km)</FormLabel>
                                  <Input
                                    type="number"
                                    value={stop.distanceFromStart || 0}
                                    onChange={(e) => updateStop(index, 'distanceFromStart', Number(e.target.value) || 0)}
                                    placeholder="0.00"
                                    size="sm"
                                    step="0.1"
                                  />
                                </FormControl>
                                <FormControl flex={1}>
                                  <FormLabel fontSize="sm">Fare from Start (TZS)</FormLabel>
                                  <Input
                                    type="number"
                                    value={fareFromStartForStop(stop.distanceFromStart || 0, formData.baseFare || FALLBACK_BASE_FARE)}
                                    placeholder="Auto-calculated"
                                    size="sm"
                                    isReadOnly
                                    bg="gray.50"
                                    title={`Tiered: base up to ${TIER_THRESHOLD_KM} km, then +${PRICE_PER_KM_AFTER_THRESHOLD}/km; rounded to 500`}
                                  />
                                </FormControl>
                              </HStack>
                            </VStack>

                            {/* Remove button */}
                            <IconButton
                              aria-label="Remove stop"
                              icon={<Trash2 size={16} />}
                              size="sm"
                              colorScheme="red"
                              variant="ghost"
                              onClick={() => removeStop(index)}
                            />
                          </HStack>
                        </CardBody>
                      </Card>
                    ))}

                    {/* Secondary Add Stop button at bottom */}
                    <Center pt={2}>
                      <Button
                        leftIcon={<Plus size={16} />}
                        size="sm"
                        colorScheme="brand"
                        variant="outline"
                        onClick={addStop}
                      >
                        Add Stop
                      </Button>
                    </Center>
                  </VStack>
                )}
              </Box>

              <HStack spacing={3} w="full" pt={4}>
                <Button
                  type="button"
                  variant="outline"
                  onClick={onClose}
                  flex={1}
                  borderRadius="xl"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  colorScheme="brand"
                  flex={1}
                  borderRadius="xl"
                  isLoading={isLoading}
                  loadingText="Updating..."
                  leftIcon={<Edit size={16} />}
                >
                  Update Route
                </Button>
              </HStack>
            </VStack>
          </form>
        </ModalBody>
      </ModalContent>
    </Modal>
  )
}

function RouteFormModal({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
  const [formData, setFormData] = useState<RouteFormData>({
    name: '',
    routeCode: '',
    startPoint: '',
    endPoint: '',
    roadNames: '',
    distanceKm: 0,
    estimatedDurationMinutes: 0,
    baseFare: FALLBACK_BASE_FARE,
    stops: []
  })
  const [isLoading, setIsLoading] = useState(false)
  const toast = useToast()
  const queryClient = useQueryClient()

  // Stops management functions
  const addStop = () => {
    const newStop: StopData = {
      name: '',
      latitude: 0,
      longitude: 0,
      address: '',
      distanceFromStart: 0,
      fareFromStart: 0,
      order: formData.stops.length + 1
    }
    setFormData({
      ...formData,
      stops: [...formData.stops, newStop]
    })
  }

  const removeStop = (index: number) => {
    const updatedStops = formData.stops.filter((_, i) => i !== index)
    // Reorder remaining stops
    const reorderedStops = updatedStops.map((stop, i) => ({
      ...stop,
      order: i + 1
    }))
    setFormData({
      ...formData,
      stops: reorderedStops
    })
  }

  const updateStop = (index: number, field: keyof StopData, value: any) => {
    setFormData((prev) => {
      const updatedStops = [...prev.stops]
      const next = { ...updatedStops[index], [field]: value } as StopData
      if (field === 'distanceFromStart') {
        next.fareFromStart = fareFromStartForStop(Number(value) || 0, prev.baseFare || FALLBACK_BASE_FARE)
      }
      updatedStops[index] = next
      return { ...prev, stops: updatedStops }
    })
  }

  const moveStopUp = (index: number) => {
    if (index === 0) return
    setFormData((prev) => {
      const updatedStops = [...prev.stops]
      const temp = updatedStops[index]
      updatedStops[index] = updatedStops[index - 1]
      updatedStops[index - 1] = temp
      const reorderedStops = updatedStops.map((stop, i) => ({
        ...stop,
        order: i + 1
      }))
      return { ...prev, stops: reorderedStops }
    })
  }

  const moveStopDown = (index: number) => {
    setFormData((prev) => {
      if (index === prev.stops.length - 1) return prev
      const updatedStops = [...prev.stops]
      const temp = updatedStops[index]
      updatedStops[index] = updatedStops[index + 1]
      updatedStops[index + 1] = temp
      const reorderedStops = updatedStops.map((stop, i) => ({
        ...stop,
        order: i + 1
      }))
      return { ...prev, stops: reorderedStops }
    })
  }

  const createRouteMutation = useMutation(
    (routeData: any) => cmsService.createRoute(routeData),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('routes')
        toast({
          title: 'Route Created',
          description: `${formData.name} has been successfully created.`,
          status: 'success',
          duration: 3000,
          isClosable: true,
        })
        onClose()
        setFormData({
          name: '',
          routeCode: '',
          startPoint: '',
          endPoint: '',
          roadNames: '',
          distanceKm: 0,
          estimatedDurationMinutes: 0,
          baseFare: FALLBACK_BASE_FARE,
          stops: []
        })
      },
      onError: (error: any) => {
        toast({
          title: 'Error',
          description: error?.response?.data?.error || 'Failed to create route. Please try again.',
          status: 'error',
          duration: 3000,
          isClosable: true,
        })
      }
    }
  )

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)

    try {
      // Validate stops data
      const hasValidStops = formData.stops.every(stop =>
        stop.name.trim() !== '' &&
        stop.latitude !== 0 &&
        stop.longitude !== 0
      )

      if (!hasValidStops) {
        toast({
          title: 'Validation Error',
          description: 'Please ensure all stops have valid names and coordinates.',
          status: 'error',
          duration: 3000,
          isClosable: true,
        })
        setIsLoading(false)
        return
      }

      const bf = formData.baseFare || FALLBACK_BASE_FARE
      const payload = {
        ...formData,
        stops: formData.stops.map((s) => ({
          ...s,
          fareFromStart: fareFromStartForStop(s.distanceFromStart || 0, bf),
        })),
      }
      await createRouteMutation.mutateAsync(payload)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Create New Route</ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          <form onSubmit={handleSubmit}>
            <VStack spacing={4}>
              <FormControl isRequired>
                <FormLabel>Route Name</FormLabel>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., Kimara - CBD"
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel>Route Code</FormLabel>
                <Input
                  value={formData.routeCode}
                  onChange={(e) => setFormData({ ...formData, routeCode: e.target.value })}
                  placeholder="e.g., KMR-CBD"
                />
              </FormControl>

              <HStack spacing={4} w="full">
                <FormControl isRequired>
                  <FormLabel>Start Point</FormLabel>
                  <Input
                    value={formData.startPoint}
                    onChange={(e) => setFormData({ ...formData, startPoint: e.target.value })}
                    placeholder="e.g., Kimara Terminal"
                  />
                </FormControl>
                <FormControl isRequired>
                  <FormLabel>End Point</FormLabel>
                  <Input
                    value={formData.endPoint}
                    onChange={(e) => setFormData({ ...formData, endPoint: e.target.value })}
                    placeholder="e.g., CBD Terminal"
                  />
                </FormControl>
              </HStack>

              <FormControl>
                <FormLabel>Road Names (comma separated)</FormLabel>
                <Input
                  value={formData.roadNames}
                  onChange={(e) => setFormData({ ...formData, roadNames: e.target.value })}
                  placeholder="e.g., Ali Hassan Mwinyi Road, New Bagamoyo Road"
                />
                <Text fontSize="xs" color="gray.500" mt={1}>
                  Enter the major roads covered by this route, separated by commas
                </Text>
              </FormControl>

              <HStack spacing={4} w="full">
                <FormControl>
                  <FormLabel>Distance (km)</FormLabel>
                  <NumberInput
                    value={formData.distanceKm}
                    onChange={(valueString) => setFormData({ ...formData, distanceKm: parseFloat(valueString) || 0 })}
                  >
                    <NumberInputField placeholder="0" />
                  </NumberInput>
                </FormControl>
                <FormControl>
                  <FormLabel>Duration (minutes)</FormLabel>
                  <NumberInput
                    value={formData.estimatedDurationMinutes}
                    onChange={(valueString) => setFormData({ ...formData, estimatedDurationMinutes: parseInt(valueString) || 0 })}
                  >
                    <NumberInputField placeholder="0" />
                  </NumberInput>
                </FormControl>
              </HStack>

              <FormControl>
                <FormLabel>Base Fare / Minimum Fare (TZS)</FormLabel>
                <NumberInput
                  value={formData.baseFare}
                  onChange={(valueString) => {
                    const v = parseInt(valueString, 10) || FALLBACK_BASE_FARE
                    setFormData((prev) => ({
                      ...prev,
                      baseFare: v,
                      stops: prev.stops.map((s) => ({
                        ...s,
                        fareFromStart: fareFromStartForStop(s.distanceFromStart || 0, v),
                      })),
                    }))
                  }}
                >
                  <NumberInputField placeholder="2000" />
                </NumberInput>
                <Text fontSize="xs" color="gray.500" mt={1}>
                  Tiered model: this amount covers up to {TIER_THRESHOLD_KM} km from the start; beyond that add TZS {PRICE_PER_KM_AFTER_THRESHOLD}/km (rounded to nearest 500). First stop stays 0 TZS.
                </Text>
              </FormControl>

              {/* Stops Management Section */}
              <Box w="full" pt={4}>
                <HStack justify="space-between" mb={4}>
                  <Heading size="md" color="gray.900">
                    Route Stops
                  </Heading>
                  <Button
                    leftIcon={<Plus size={16} />}
                    size="sm"
                    colorScheme="brand"
                    variant="outline"
                    onClick={addStop}
                  >
                    Add Stop
                  </Button>
                </HStack>

                {formData.stops.length === 0 ? (
                  <Card bg="gray.50" border="2px dashed" borderColor="gray.200">
                    <CardBody>
                      <Center py={8}>
                        <VStack spacing={4}>
                          <Box p={3} bg="gray.100" borderRadius="full">
                            <MapPin size={24} color="var(--chakra-colors-gray-500)" />
                          </Box>
                          <VStack spacing={2}>
                            <Text fontWeight="medium" color="gray.700">
                              No stops added yet
                            </Text>
                            <Text fontSize="sm" color="gray.500" textAlign="center">
                              Add stops along the route to define the complete journey
                            </Text>
                          </VStack>
                          <Button
                            leftIcon={<Plus size={16} />}
                            colorScheme="brand"
                            variant="outline"
                            onClick={addStop}
                            size="sm"
                          >
                            Add First Stop
                          </Button>
                        </VStack>
                      </Center>
                    </CardBody>
                  </Card>
                ) : (
                  <VStack spacing={3} align="stretch">
                    {formData.stops.map((stop, index) => (
                      <Card key={index} bg="white" border="1px solid" borderColor="gray.200">
                        <CardBody>
                          <HStack spacing={4} align="start">
                            {/* Order indicator and drag handle */}
                            <VStack spacing={1}>
                              <Box
                                p={2}
                                bg="brand.100"
                                color="brand.600"
                                borderRadius="md"
                                fontSize="sm"
                                fontWeight="bold"
                                minW="32px"
                                textAlign="center"
                              >
                                {stop.order}
                              </Box>
                              <HStack spacing={1}>
                                <IconButton
                                  aria-label="Move up"
                                  icon={<ArrowUp size={12} />}
                                  size="xs"
                                  variant="ghost"
                                  onClick={() => moveStopUp(index)}
                                  isDisabled={index === 0}
                                />
                                <IconButton
                                  aria-label="Move down"
                                  icon={<ArrowDown size={12} />}
                                  size="xs"
                                  variant="ghost"
                                  onClick={() => moveStopDown(index)}
                                  isDisabled={index === formData.stops.length - 1}
                                />
                              </HStack>
                            </VStack>

                            {/* Stop details */}
                            <VStack spacing={3} flex={1} align="stretch">
                              <HStack spacing={3}>
                                <FormControl flex={1}>
                                  <FormLabel fontSize="sm">Stop Name</FormLabel>
                                  <Input
                                    value={stop.name}
                                    onChange={(e) => updateStop(index, 'name', e.target.value)}
                                    placeholder="e.g., Mwenge"
                                    size="sm"
                                  />
                                </FormControl>
                                <FormControl flex={1}>
                                  <FormLabel fontSize="sm">Address/Landmark</FormLabel>
                                  <Input
                                    value={stop.address || ''}
                                    onChange={(e) => updateStop(index, 'address', e.target.value)}
                                    placeholder="e.g., Near Mwenge Market"
                                    size="sm"
                                  />
                                </FormControl>
                              </HStack>

                              <HStack spacing={3}>
                                <FormControl flex={1}>
                                  <FormLabel fontSize="sm">Latitude</FormLabel>
                                  <Input
                                    type="number"
                                    value={stop.latitude}
                                    onChange={(e) => updateStop(index, 'latitude', Number(e.target.value) || 0)}
                                    placeholder="0.00000000"
                                    size="sm"
                                    step="0.0000001"
                                  />
                                </FormControl>
                                <FormControl flex={1}>
                                  <FormLabel fontSize="sm">Longitude</FormLabel>
                                  <Input
                                    type="number"
                                    value={stop.longitude}
                                    onChange={(e) => updateStop(index, 'longitude', Number(e.target.value) || 0)}
                                    placeholder="0.00000000"
                                    size="sm"
                                    step="0.0000001"
                                  />
                                </FormControl>
                              </HStack>

                              <HStack spacing={3}>
                                <FormControl flex={1}>
                                  <FormLabel fontSize="sm">Distance from Start (km)</FormLabel>
                                  <Input
                                    type="number"
                                    value={stop.distanceFromStart || 0}
                                    onChange={(e) => updateStop(index, 'distanceFromStart', Number(e.target.value) || 0)}
                                    placeholder="0.00"
                                    size="sm"
                                    step="0.1"
                                  />
                                </FormControl>
                                <FormControl flex={1}>
                                  <FormLabel fontSize="sm">Fare from Start (TZS)</FormLabel>
                                  <Input
                                    type="number"
                                    value={fareFromStartForStop(stop.distanceFromStart || 0, formData.baseFare || FALLBACK_BASE_FARE)}
                                    placeholder="Auto-calculated"
                                    size="sm"
                                    isReadOnly
                                    bg="gray.50"
                                    title={`Tiered: base up to ${TIER_THRESHOLD_KM} km, then +${PRICE_PER_KM_AFTER_THRESHOLD}/km; rounded to 500`}
                                  />
                                </FormControl>
                              </HStack>
                            </VStack>

                            {/* Remove button */}
                            <IconButton
                              aria-label="Remove stop"
                              icon={<Trash2 size={16} />}
                              size="sm"
                              colorScheme="red"
                              variant="ghost"
                              onClick={() => removeStop(index)}
                            />
                          </HStack>
                        </CardBody>
                      </Card>
                    ))}

                    {/* Secondary Add Stop button at bottom */}
                    <Center pt={2}>
                      <Button
                        leftIcon={<Plus size={16} />}
                        size="sm"
                        colorScheme="brand"
                        variant="outline"
                        onClick={addStop}
                      >
                        Add Stop
                      </Button>
                    </Center>
                  </VStack>
                )}
              </Box>

              <HStack spacing={4} w="full" pt={4}>
                <Button variant="outline" onClick={onClose} flex={1}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  colorScheme="brand"
                  isLoading={isLoading}
                  loadingText="Creating..."
                  flex={1}
                  isDisabled={formData.stops.length === 0}
                >
                  Create Route
                </Button>
              </HStack>
            </VStack>
          </form>
        </ModalBody>
      </ModalContent>
    </Modal>
  )
}

export default function RoutesPage() {
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedStatus, setSelectedStatus] = useState('all')
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage] = useState(10)
  const { isOpen, onOpen, onClose } = useDisclosure()
  const { isOpen: isViewOpen, onOpen: onViewOpen, onClose: onViewClose } = useDisclosure()
  const { isOpen: isEditOpen, onOpen: onEditOpen, onClose: onEditClose } = useDisclosure()
  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure()
  const [selectedRoute, setSelectedRoute] = useState<any>(null)
  const toast = useToast()
  const queryClient = useQueryClient()

  // Fetch real data from API
  const { data: routesData, isLoading: routesLoading } = useQuery(
    'routes',
    () => cmsService.getRoutes(),
    {
      retry: false,
    }
  )

  const { data: dashboardStats } = useQuery(
    'dashboard-stats',
    () => cmsService.getDashboardStats(),
    {
      retry: false,
    }
  )

  // Use real data only - no fallbacks
  const routes: any[] = routesData?.data || []

  const stats = [
    { title: "Total Routes", value: dashboardStats?.data?.totalRoutes || routes.length, color: "blue", icon: Route },
    { title: "Active Routes", value: dashboardStats?.data?.activeRoutes || routes.filter(r => r?.active).length, color: "green", icon: Route },
    { title: "Inactive Routes", value: routes.filter(r => !r?.active).length, color: "red", icon: X },
  ]

  const filteredRoutes = routes.filter((route: RouteData) => {
    // Add null/undefined checks to prevent toLowerCase errors
    const name = route?.name?.toLowerCase() || ''
    const code = route?.routeCode?.toLowerCase() || ''
    const startPoint = route?.startPoint?.toLowerCase() || ''
    const endPoint = route?.endPoint?.toLowerCase() || ''

    const matchesSearch = name.includes(searchTerm.toLowerCase()) ||
      code.includes(searchTerm.toLowerCase()) ||
      startPoint.includes(searchTerm.toLowerCase()) ||
      endPoint.includes(searchTerm.toLowerCase())
    const matchesStatus = selectedStatus === 'all' ||
      (selectedStatus === 'active' && route?.active) ||
      (selectedStatus === 'inactive' && !route?.active)
    return matchesSearch && matchesStatus
  })

  // Pagination logic
  const totalPages = Math.ceil(filteredRoutes.length / itemsPerPage)
  const startIndex = (currentPage - 1) * itemsPerPage
  const endIndex = startIndex + itemsPerPage
  const currentRoutes = filteredRoutes.slice(startIndex, endIndex)

  const handleViewRoute = (route: any) => {
    setSelectedRoute(route)
    onViewOpen()
  }

  const handleEditRoute = (route: any) => {
    setSelectedRoute(route)
    onEditOpen()
  }

  const handleDeleteRoute = (route: any) => {
    setSelectedRoute(route)
    onDeleteOpen()
  }

  const confirmDeleteRoute = async () => {
    if (!selectedRoute) return

    try {
      await cmsService.deleteRoute(selectedRoute.id)
      queryClient.invalidateQueries('routes')
      
      toast({
        title: 'Route Deleted',
        description: 'Route has been successfully deleted.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      })
      
      onDeleteClose()
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error?.response?.data?.error?.message || 'Failed to delete route.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      })
    }
  }

  const handleStatusToggle = async (routeId: string, currentStatus: boolean) => {
    try {
      await cmsService.updateRouteStatus(routeId, !currentStatus)

      // Invalidate and refetch routes data
      queryClient.invalidateQueries('routes')

      toast({
        title: 'Status Updated',
        description: 'Route status has been updated successfully.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      })
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error?.response?.data?.error || 'Failed to update route status.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      })
    }
  }

  // Show loading state
  if (routesLoading) {
    return (
      <Center h="64">
        <Spinner size="lg" color="brand.500" />
      </Center>
    )
  }

  return (
    <VStack spacing={6} align="stretch">
      {/* Header */}
      <Flex align="center" justify="space-between" flexDirection={{ base: 'column', md: 'row' }} gap={4}>
        <VStack align={{ base: 'center', md: 'start' }} spacing={2}>
          <Heading size="xl" color="gray.900">
            Routes Management
          </Heading>
          <Text color="gray.600">
            Manage your daladala routes and pricing
          </Text>
        </VStack>
        <HStack spacing={3}>
          <Button
            leftIcon={<Download size={16} />}
            variant="outline"
            size="sm"
          >
            Export
          </Button>
          <Button
            leftIcon={<Plus size={16} />}
            colorScheme="brand"
            onClick={onOpen}
          >
            Add Route
          </Button>
        </HStack>
      </Flex>

      {/* Stats Cards */}
      <SimpleGrid columns={{ base: 2, md: 3 }} spacing={6}>
        {stats.map((stat, index) => (
          <Card key={index} bg="white" shadow="sm">
            <CardBody>
              <HStack justify="space-between">
                <VStack align="start" spacing={1}>
                  <Text fontSize="sm" color="gray.600" fontWeight="medium">
                    {stat.title}
                  </Text>
                  <Text fontSize="2xl" fontWeight="bold" color="gray.900">
                    {stat.value}
                  </Text>
                </VStack>
                <Box
                  p={3}
                  borderRadius="lg"
                  bg={`${stat.color}.100`}
                  color={`${stat.color}.600`}
                >
                  <stat.icon size={24} />
                </Box>
              </HStack>
            </CardBody>
          </Card>
        ))}
      </SimpleGrid>

      {/* Filters and Search */}
      <Card bg="white" shadow="sm">
        <CardBody>
          <HStack spacing={4} flexDirection={{ base: 'column', md: 'row' }}>
            <InputGroup maxW="400px">
              <InputLeftElement pointerEvents="none">
                <Search size={16} color="var(--chakra-colors-gray-400)" />
              </InputLeftElement>
              <Input
                placeholder="Search routes by name, code, or stops..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </InputGroup>
            <Select
              maxW="200px"
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
            >
              <option value="all">All Routes</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </Select>
          </HStack>
        </CardBody>
      </Card>

      {/* Routes Table */}
      <Card bg="white" shadow="sm">
        <CardHeader>
          <Flex justify="space-between" align="center">
            <Heading size="md" color="gray.900">
              Routes List
            </Heading>
            <Text color="gray.600" fontSize="sm">
              {filteredRoutes.length} route{filteredRoutes.length !== 1 ? 's' : ''}
            </Text>
          </Flex>
        </CardHeader>
        <CardBody>
          <Box overflowX="auto">
            <Table variant="simple">
              <Thead>
                <Tr>
                  <Th borderBottom="2px solid" borderColor="gray.200" py={4}>Route</Th>
                  <Th borderBottom="2px solid" borderColor="gray.200" py={4}>Code</Th>
                  <Th borderBottom="2px solid" borderColor="gray.200" py={4}>Stops</Th>
                  <Th borderBottom="2px solid" borderColor="gray.200" py={4}>Distance</Th>
                  <Th borderBottom="2px solid" borderColor="gray.200" py={4}>Base Fare</Th>
                  <Th borderBottom="2px solid" borderColor="gray.200" py={4}>Status</Th>
                  <Th borderBottom="2px solid" borderColor="gray.200" py={4}>Created</Th>
                  <Th borderBottom="2px solid" borderColor="gray.200" py={4}>Actions</Th>
                </Tr>
              </Thead>
              <Tbody>
                {filteredRoutes.length === 0 ? (
                  <Tr>
                    <Td colSpan={8} py={12}>
                      <Center>
                        <VStack spacing={4}>
                          <Box p={4} bg="gray.100" borderRadius="full">
                            <Route size={48} color="var(--chakra-colors-gray-500)" />
                          </Box>
                          <VStack spacing={2}>
                            <Heading size="md" color="gray.900">
                              No routes found
                            </Heading>
                            <Text color="gray.600" textAlign="center">
                              {searchTerm || selectedStatus !== 'all'
                                ? 'No routes match your current filters.'
                                : 'Get started by creating your first route.'
                              }
                            </Text>
                          </VStack>
                          {!searchTerm && selectedStatus === 'all' && (
                            <Button
                              colorScheme="brand"
                              leftIcon={<Plus size={16} />}
                              onClick={onOpen}
                              borderRadius="xl"
                            >
                              Create Your First Route
                            </Button>
                          )}
                        </VStack>
                      </Center>
                    </Td>
                  </Tr>
                ) : (
                  currentRoutes.map((route: RouteData) => (
                    <Tr key={route.id} _hover={{ bg: 'gray.50' }} borderBottom="1px solid" borderColor="gray.100">
                      <Td py={4}>
                        <HStack spacing={3}>
                          <Avatar size="sm" bg="green.100" color="green.600" icon={<Route size={16} />} />
                          <VStack align="start" spacing={0}>
                            <Text fontWeight="medium" color="gray.900">
                              {route.name}
                            </Text>
                            <Text fontSize="sm" color="gray.600">
                              {route.startPoint} → {route.endPoint}
                            </Text>
                            {(route.roadNames && route.roadNames.length > 0) && (
                              <Text fontSize="xs" color="brand.500" fontStyle="italic" mt={1}>
                                Via: {route.roadNames.join(', ')}
                              </Text>
                            )}
                          </VStack>
                        </HStack>
                      </Td>
                      <Td py={4}>
                        <Badge
                          colorScheme="blue"
                          variant="subtle"
                          px={2}
                          py={1}
                          borderRadius="md"
                        >
                          {route.routeCode}
                        </Badge>
                      </Td>
                      <Td py={4}>
                        {route.stops && route.stops.length > 0 ? (
                          <VStack align="start" spacing={1}>
                            {route.stops.slice(0, 3).map((stop, index) => (
                              <HStack key={stop.id} spacing={2}>
                                <Box
                                  p={1}
                                  bg="brand.100"
                                  color="brand.600"
                                  borderRadius="sm"
                                  fontSize="xs"
                                  fontWeight="bold"
                                  minW="16px"
                                  textAlign="center"
                                >
                                  {stop.stopOrder}
                                </Box>
                                <MapPin size={10} color="var(--chakra-colors-gray-500)" />
                                <Text fontSize="sm" color="gray.900">
                                  {stop.name}
                                </Text>
                              </HStack>
                            ))}
                            {route.stops.length > 3 && (
                              <Text fontSize="xs" color="gray.500" fontStyle="italic">
                                +{route.stops.length - 3} more stops
                              </Text>
                            )}
                          </VStack>
                        ) : (
                          <VStack align="start" spacing={1}>
                            <HStack spacing={2}>
                              <MapPin size={12} color="var(--chakra-colors-gray-500)" />
                              <Text fontSize="sm" color="gray.900">
                                {route.startPoint}
                              </Text>
                            </HStack>
                            <HStack spacing={2}>
                              <Navigation size={12} color="var(--chakra-colors-gray-500)" />
                              <Text fontSize="sm" color="gray.900">
                                {route.endPoint}
                              </Text>
                            </HStack>
                          </VStack>
                        )}
                      </Td>
                      <Td py={4}>
                        <Text fontSize="sm" color="gray.600">
                          {route.distanceKm ? `${route.distanceKm} km` : 'N/A'}
                        </Text>
                      </Td>
                      <Td py={4}>
                        <HStack spacing={2}>
                          <DollarSign size={14} color="var(--chakra-colors-green-500)" />
                          <Text fontSize="sm" color="gray.900" fontWeight="medium">
                            {route.baseFare ? `TZS ${route.baseFare.toLocaleString()}` : 'N/A'}
                          </Text>
                        </HStack>
                      </Td>
                      <Td py={4}>
                        <Badge
                          colorScheme={route.active ? 'green' : 'red'}
                          variant="subtle"
                          px={3}
                          py={1}
                          borderRadius="full"
                        >
                          {route.active ? 'Active' : 'Inactive'}
                        </Badge>
                      </Td>
                      <Td py={4}>
                        <Text fontSize="sm" color="gray.600">
                          {route.createdAt && !isNaN(new Date(route.createdAt).getTime())
                            ? new Date(route.createdAt).toLocaleDateString()
                            : 'N/A'
                          }
                        </Text>
                      </Td>
                      <Td py={4}>
                        <HStack spacing={1}>
                          <Tooltip label="View Details">
                            <IconButton
                              aria-label="View"
                              icon={<Eye size={16} />}
                              size="sm"
                              variant="ghost"
                              colorScheme="blue"
                              onClick={() => handleViewRoute(route)}
                            />
                          </Tooltip>
                          <Tooltip label="Edit Route">
                            <IconButton
                              aria-label="Edit"
                              icon={<Edit size={16} />}
                              size="sm"
                              variant="ghost"
                              colorScheme="blue"
                              onClick={() => handleEditRoute(route)}
                            />
                          </Tooltip>
                          <Tooltip label={route.active ? 'Deactivate' : 'Activate'}>
                            <Button
                              size="sm"
                              colorScheme={route.active ? 'red' : 'green'}
                              variant="outline"
                              onClick={() => handleStatusToggle(route.id, route.active)}
                            >
                              {route.active ? 'Deactivate' : 'Activate'}
                            </Button>
                          </Tooltip>
                          <Tooltip label="Delete Route">
                            <IconButton
                              aria-label="Delete"
                              icon={<Trash2 size={16} />}
                              size="sm"
                              variant="ghost"
                              colorScheme="red"
                              onClick={() => handleDeleteRoute(route)}
                            />
                          </Tooltip>
                        </HStack>
                      </Td>
                    </Tr>
                  ))
                )}
              </Tbody>
            </Table>
          </Box>
        </CardBody>

        {/* Pagination */}
        {totalPages > 1 && (
          <HStack justify="center" py={4} borderTop="1px solid" borderColor="gray.100">
            <Button
              size="sm"
              variant="outline"
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              isDisabled={currentPage === 1}
            >
              Previous
            </Button>
            <Text fontSize="sm" color="gray.600" px={4}>
              Page {currentPage} of {totalPages}
            </Text>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              isDisabled={currentPage === totalPages}
            >
              Next
            </Button>
          </HStack>
        )}
      </Card>

      {/* Add Route Modal */}
      <RouteFormModal isOpen={isOpen} onClose={onClose} />

      {/* View Route Modal */}
      <RouteViewModal isOpen={isViewOpen} onClose={onViewClose} route={selectedRoute} />

      {/* Edit Route Modal */}
      <RouteEditModal isOpen={isEditOpen} onClose={onEditClose} route={selectedRoute} />

      {/* Delete Route Dialog */}
      <RouteDeleteDialog 
        isOpen={isDeleteOpen} 
        onClose={onDeleteClose} 
        onConfirm={confirmDeleteRoute}
        route={selectedRoute}
      />
    </VStack>
  )
}